/**
 * Proactive token refresh timer.
 * Schedules a refresh 1 minute before the access token expires.
 *
 * This file does not import `./http`. `http.ts` owns the single-flight refresh
 * and the logout helper, and hands both in through `configureRefreshTimer`.
 * That keeps the import one-way: http.ts -> token-refresh.ts.
 */

interface RefreshTimerDeps {
  /** The shared single-flight refresh. On success it restarts this timer itself. */
  refresh: () => Promise<void>;
  /** The shared real logout: server logout, clear caches, go to /login?to=. */
  endSession: () => void;
}

/** Waits before each retry after a network error, 5xx or 429. About 30s in total. */
const RETRY_DELAYS_MS = [5_000, 10_000, 15_000];

/** These mean the refresh cookie is dead. Retrying cannot help. */
const DEAD_SESSION_STATUSES = new Set([400, 401, 403]);

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let deps: RefreshTimerDeps | null = null;

export const configureRefreshTimer = (next: RefreshTimerDeps) => {
  deps = next;
};

const statusOf = (error: unknown): number | undefined =>
  (error as { status?: number } | null)?.status;

const schedule = (delayMs: number, run: () => void) => {
  stopRefreshTimer();
  refreshTimerId = setTimeout(run, delayMs);
};

const runRefresh = (attempt: number) => {
  if (!deps) return;
  const { refresh, endSession } = deps;
  refresh().catch((error: unknown) => {
    const status = statusOf(error);
    if (status !== undefined && DEAD_SESSION_STATUSES.has(status)) {
      endSession();
      return;
    }
    // Network error, 5xx or 429: the session may still be fine. Try again.
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay === undefined) {
      endSession();
      return;
    }
    schedule(delay, () => runRefresh(attempt + 1));
  });
};

export const startRefreshTimer = (expiresIn: number) => {
  // Refresh 60 seconds before expiry, minimum 10 seconds
  const delaySeconds = Math.max(expiresIn - 60, 10);
  schedule(delaySeconds * 1000, () => runRefresh(0));
};

export const stopRefreshTimer = () => {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};
